export interface FrameStats {
  fps: number;
  frameMs: number;
  workMs: number;
  maxWorkMs: number;
}

/**
 * The single animation loop of the game. Simulation runs on a fixed timestep
 * (60 Hz) via an accumulator; rendering happens once per animation frame.
 * Rendering stops entirely while the tab is hidden.
 */
export class GameLoop {
  readonly fixedStepMs = 1000 / 60;
  private acc = 0;
  private last = 0;
  private raf = 0;
  private running = false;
  private frameSkip = 0;
  private skipCounter = 0;
  /** Maximum fixed steps per frame before dropping time (spiral-of-death guard). */
  private readonly maxSteps = 5;

  private statFrames = 0;
  private statWork = 0;
  private statMaxWork = 0;
  private statStart = 0;
  stats: FrameStats = { fps: 0, frameMs: 0, workMs: 0, maxWorkMs: 0 };

  constructor(
    private readonly update: (dtMs: number) => void,
    private readonly render: (alpha: number) => void,
  ) {
    document.addEventListener('visibilitychange', this.onVisibility);
  }

  /** 60 = every frame, 30 = render every other frame (battery saver). */
  setFpsCap(cap: 30 | 60): void {
    this.frameSkip = cap === 30 ? 1 : 0;
  }

  start(): void {
    if (this.running) return;
    this.running = true;
    this.last = performance.now();
    this.statStart = this.last;
    this.acc = 0;
    this.raf = requestAnimationFrame(this.frame);
  }

  stop(): void {
    this.running = false;
    cancelAnimationFrame(this.raf);
  }

  private onVisibility = (): void => {
    if (document.hidden) {
      cancelAnimationFrame(this.raf);
    } else if (this.running) {
      this.last = performance.now();
      this.acc = 0;
      this.raf = requestAnimationFrame(this.frame);
    }
  };

  private frame = (now: number): void => {
    if (!this.running || document.hidden) return;
    this.raf = requestAnimationFrame(this.frame);

    if (this.frameSkip > 0 && this.skipCounter++ % (this.frameSkip + 1) !== 0) return;

    const start = performance.now();
    let dt = now - this.last;
    this.last = now;
    if (dt > 250) dt = 250;
    this.acc += dt;
    let steps = 0;
    while (this.acc >= this.fixedStepMs && steps < this.maxSteps) {
      this.update(this.fixedStepMs);
      this.acc -= this.fixedStepMs;
      steps++;
    }
    if (steps === this.maxSteps) this.acc = 0;
    this.render(this.acc / this.fixedStepMs);

    const work = performance.now() - start;
    this.statFrames++;
    this.statWork += work;
    if (work > this.statMaxWork) this.statMaxWork = work;
    const elapsed = now - this.statStart;
    if (elapsed >= 500) {
      this.stats = {
        fps: (this.statFrames * 1000) / elapsed,
        frameMs: elapsed / this.statFrames,
        workMs: this.statWork / this.statFrames,
        maxWorkMs: this.statMaxWork,
      };
      this.statFrames = 0;
      this.statWork = 0;
      this.statMaxWork = 0;
      this.statStart = now;
    }
  };

  destroy(): void {
    this.stop();
    document.removeEventListener('visibilitychange', this.onVisibility);
  }
}
